import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ChildrenProps } from '@dfl/mui-react-common';
import { useUser } from '@dfl/react-security';
import { useNotifications } from 'modules/notification/hooks/useNotifications';
import { ExportStatusResponse } from 'modules/export/interfaces/common-export';
import { EXPORT_STATUS_ENUM } from 'modules/export/interfaces/export.enum';
import { isTerminal } from 'modules/export/utils/export-status.utils';
import {
  forgetTrackedExport,
  isTracked,
  markProcessed,
  trackExport,
  wasProcessed,
} from 'modules/export/storage/tracked-exports.storage';
import { clearExportState } from 'modules/export/storage/last-export.storage';
import {
  BACKGROUND_EXPORT_QUEUE_KEY,
  BackgroundExportItem,
  getBackgroundExportQueue,
} from 'modules/export/storage/background-export-queue.storage';
import DownloadFile from 'modules/export/components/DownlandFile/DownloadFile';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Close } from '@mui/icons-material';
import { useBackgroundExportPolling } from 'modules/export/hooks/useBackgroundExportPolling';
import { INotification, NOTIFICATION_TYPE } from 'modules/notification/interfaces/INotification';
import toast from 'react-hot-toast';
import NotificationToastItem from 'modules/notification/components/NotificationDrawer/NotificationToastItem';

interface ExportConfig {
  title?: string;
  confirmation?: string;
  total?: number;
  selected?: readonly string[] | undefined;
  exportHook: () => Promise<ExportStatusResponse>;
  downloadFn: (path: string, name?: string) => Promise<void>;
  storageKey?: string;
}

interface CentralizedExportContextValue {
  triggerExport: (config: ExportConfig) => void;
  isModalOpen: boolean; // Expose modal state for notification system
  backgroundQueue: BackgroundExportItem[];
  updateBackgroundQueue: (queue: BackgroundExportItem[]) => void;
  pollingData: BackgroundExportItem[] | undefined; // Expose polling data
  exportData: ExportStatusResponse | null; // Expose current export data
}

interface ExportState {
  isInfoModalOpen: boolean;
  isProcessing: boolean;
  isDownloadModalOpen: boolean;
  config: ExportConfig | null;
  exportData: ExportStatusResponse | null;
  error: any;
  currentExportId: string | null;
}

const CentralizedExportContext = createContext<CentralizedExportContextValue | undefined>(undefined);

// Global download lock to prevent multiple downloads
if (typeof window !== 'undefined') {
  if (!window.exportDownloadLock) {
    window.exportDownloadLock = new Set<string>();
  }
  if (!window.downloadTimestamps) {
    window.downloadTimestamps = new Map<string, number>();
  }
}

declare global {
  interface Window {
    exportDownloadLock?: Set<string>;
    downloadTimestamps?: Map<string, number>;
  }
}

// Internal Export Information Alert Modal Component
const InternalExportInformationAlert = ({
  isOpen,
  config,
  onExport,
  onClose,
  isProcessing,
  error,
}: // exportData,
{
  isOpen: boolean;
  config: ExportConfig;
  onExport: () => void;
  onClose: () => void;
  isProcessing: boolean;
  error: any;
  exportData?: ExportStatusResponse | null;
}) => {
  const { t } = useTranslation('common');
  const [notShowMore, setNotShowMore] = useState(false);

  if (!isOpen) return null;

  const {
    title = 'common:exportDialog.title',
    confirmation = 'common:exportDialog.confirmation',
    total,
    selected,
  } = config;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Button aria-label='close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} variant='text'>
          <Close />
        </Button>
      </DialogTitle>
      <DialogContent sx={{ padding: '20px 40px' }}>
        {error && (
          <>
            <Typography color='error' mb={error.reference ? 0.5 : 2}>
              {error.message || t('exportDialog.failed', { defaultValue: 'La exportacion no se pudo completar' })}
            </Typography>
            {/* El reference es el enum del dominio: es lo que hace util un
                reporte de soporte y lo unico agrupable para alertas. */}
            {error.reference && (
              <Typography variant='caption' color='text.secondary' display='block' mb={2}>
                {error.reference}
              </Typography>
            )}
          </>
        )}
        <Typography variant='h2' mb={3} fontSize={24}>
          {t(title)}
        </Typography>
        <Typography mb={1}>
          {t(confirmation, {
            total: selected?.length || total || 0,
          })}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={notShowMore}
              onChange={(e) => {
                setNotShowMore(e.target.checked);
              }}
            />
          }
          label={t('notShowMessage')}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={isProcessing}>
          {t('cancel')}
        </Button>
        <LoadingButton loading={isProcessing} onClick={onExport} variant='contained'>
          {t('export')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export const CentralizedExportProvider = ({ children }: ChildrenProps) => {
  const { user } = useUser();
  const userId = user?._id || user?.id || '';

  // Initialize background queue for current user
  const [backgroundQueue, setBackgroundQueue] = useState<BackgroundExportItem[]>(() =>
    getBackgroundExportQueue(userId),
  );

  // const { t } = useTranslation('notification');

  const [exportState, setExportState] = useState<ExportState>({
    isInfoModalOpen: false,
    isProcessing: false,
    isDownloadModalOpen: false,
    config: null,
    exportData: null,
    error: null,
    currentExportId: null,
  });

  const downloadLockRef = useRef<string | null>(null);
  const exportIdRef = useRef<string | null>(null);
  const pendingNotificationsRef = useRef<INotification[]>([]);

  const closeAllModals = useCallback(() => {
    // Clear download lock for current export
    if (downloadLockRef.current) {
      window.exportDownloadLock?.delete(downloadLockRef.current);
      window.downloadTimestamps?.delete(downloadLockRef.current);
      downloadLockRef.current = null;
    }

    // Solo se deja de seguir ESTE export, no todos: puede haber otros en
    // vuelo y antes se perdia su rastro al cerrar cualquier modal.
    if (exportIdRef.current) {
      forgetTrackedExport(exportIdRef.current);
      exportIdRef.current = null;
    }
    clearExportState();

    // Show pending notifications that were missed while modal was open
    if (pendingNotificationsRef.current.length > 0) {
      pendingNotificationsRef.current.forEach((notification) => {
        const toastId = `pending-export-${notification._id}`;

        toast.dismiss(toastId);

        if (notification.type === NOTIFICATION_TYPE.EXPORT_DONE) {
          toast.custom(
            (t) => (
              <NotificationToastItem
                notification={notification}
                toastId={toastId}
                visible={t.visible}
                onDismiss={() => { toast.dismiss(toastId); }}
              />
            ),
            {
              duration: 4000,
              id: toastId,
              position: 'top-right',
            },
          );
        } else if (notification.type === NOTIFICATION_TYPE.EXPORT_FAILED) {
          toast.custom(
            (t) => (
              <NotificationToastItem
                notification={notification}
                toastId={toastId}
                visible={t.visible}
                onDismiss={() => { toast.dismiss(toastId); }}
              />
            ),
            {
              duration: 6000,
              id: toastId,
              position: 'top-right',
            },
          );
        }
      });

      // Clear pending notifications after showing them
      pendingNotificationsRef.current = [];
    }

    setExportState({
      isInfoModalOpen: false,
      isProcessing: false,
      isDownloadModalOpen: false,
      config: null,
      exportData: null,
      error: null,
      currentExportId: null,
    });
  }, []);

  const handleAutoDownload = useCallback(
    (data: ExportStatusResponse, downloadFn?: (path: string, name?: string) => Promise<void>) => {
      if (!downloadFn || !data._id) {
        return;
      }

      // Check if download was already attempted for this export
      if (window.exportDownloadLock?.has(data._id)) {
        return;
      }

      // Prevent multiple downloads of the same file within 2 seconds
      const now = Date.now();
      const lastDownload = window.downloadTimestamps?.get(data._id);
      if (lastDownload && now - lastDownload < 2000) {
        return;
      }

      // Lock this export for download and set timestamp
      window.exportDownloadLock?.add(data._id);
      window.downloadTimestamps?.set(data._id, now);
      downloadLockRef.current = data._id;

      downloadFn(data._id, data.name)
        .then(() => {
          // Close all modals after successful download
          closeAllModals();
        })
        .catch((error) => {
          // Remove lock on failure so it can be retried
          window.exportDownloadLock?.delete(data._id);
          window.downloadTimestamps?.delete(data._id);
          downloadLockRef.current = null;
          console.error('Download failed:', error);
        });
    },
    [closeAllModals],
  );

  // Generate generic file name based on export ID
  const generateGenericFileName = (exportId: string): string => {
    const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return `export_${timestamp}_${exportId.slice(-8)}`;
  };

  const updateBackgroundQueue = useCallback(
    (queue: BackgroundExportItem[]) => {
      setBackgroundQueue(queue);
      // Persist to localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem(BACKGROUND_EXPORT_QUEUE_KEY, JSON.stringify(queue));
        } catch (error) {
          console.warn('Failed to update background export queue:', error);
        }
      }
    },
    [setBackgroundQueue],
  );

  // Use the centralized background export polling hook
  const { queue: pollingData } = useBackgroundExportPolling(
    backgroundQueue || [],
    updateBackgroundQueue,
    (exportId, exportData) => {
      // Handle export completion callback
      const normalizedData: ExportStatusResponse = {
        _id: exportData._id,
        name: exportData.name || generateGenericFileName(exportData._id),
        status: exportData.status === 'ERROR' ? EXPORT_STATUS_ENUM.FAILED : EXPORT_STATUS_ENUM.DONE,
      };

      // Este aviso interesa si seguimos ese export, sea el ultimo que se
      // pidio o uno anterior que sigue en vuelo. Antes solo se atendia el
      // de un unico slot global, asi que pedir otro perdia el rastro.
      if (isTracked(normalizedData._id)) {
        if (wasProcessed(normalizedData._id)) {
          return;
        }

        if (isTerminal(normalizedData.status)) {
          markProcessed(normalizedData._id);
        }

        // Check if ANY modal is currently open (both info and download modals)
        const isAnyModalOpen = exportState.isInfoModalOpen || exportState.isDownloadModalOpen;

        if (!isAnyModalOpen) {
          // Update state but keep modals closed
          setExportState((prev) => ({
            ...prev,
            exportData: normalizedData,
            isInfoModalOpen: false,
            isDownloadModalOpen: false,
          }));
        } else {
          // If status is DONE, close modal and trigger automatic download
          if (normalizedData.status === 'DONE' && exportState.config?.downloadFn) {
            // Update state and close modal
            setExportState((prev) => ({
              ...prev,
              exportData: normalizedData,
              isInfoModalOpen: false,
              isDownloadModalOpen: false,
            }));

            // Trigger automatic download
            handleAutoDownload(normalizedData, exportState.config.downloadFn);
          } else if (normalizedData.status === 'ERROR') {
            setExportState((prev) => {
              return {
                ...prev,
                exportData: normalizedData,
                isInfoModalOpen: false,
                isDownloadModalOpen: false,
              };
            });
          } else {
            setExportState((prev) => ({
              ...prev,
              exportData: normalizedData,
              isInfoModalOpen: false,
              isDownloadModalOpen: true,
            }));
          }
        }
      }
    },
  );

  // WebSocket integration for automatic download
  useNotifications({
    autoConnect: true,
    syncWithReactQuery: false,
    onExportDone: useCallback(
      (exportDataFromSocket: any) => {
        const normalizedData: ExportStatusResponse = {
          _id: exportDataFromSocket?._id || exportDataFromSocket?.data?._id,
          name: exportDataFromSocket?.name || exportDataFromSocket?.data?.name,
          status: exportDataFromSocket?.status || exportDataFromSocket?.data?.status,
          path: exportDataFromSocket?.path || exportDataFromSocket?.data?.path,
          createdAt: exportDataFromSocket?.createdAt,
          error: exportDataFromSocket?.error || exportDataFromSocket?.data?.error,
        };

        // Update background export queue status if this export is in the queue
        if (normalizedData._id && normalizedData.status) {
          const currentQueue = backgroundQueue || [];
          let updatedQueue: BackgroundExportItem[];

          if (normalizedData.status === 'DONE' || normalizedData.status === 'ERROR') {
            // Remove completed or failed exports from the queue
            updatedQueue = currentQueue.filter((item) => item._id !== normalizedData._id);
          } else {
            // Update status for pending/in_progress exports
            updatedQueue = currentQueue.map((item) =>
              item._id === normalizedData._id ? { ...item, status: normalizedData.status } : item,
            );
          }

          if (JSON.stringify(currentQueue) !== JSON.stringify(updatedQueue)) {
            setBackgroundQueue(updatedQueue);
          }
        }

        // Este aviso interesa si seguimos ese export, sea el ultimo que se
        // pidio o uno anterior que sigue en vuelo.
        if (isTracked(normalizedData._id)) {
          if (wasProcessed(normalizedData._id)) {
            return;
          }

          // Solo se marca procesado en un estado terminal. Antes se marcaba con
          // el PRIMER aviso que llegase, de cualquier tipo, y a partir de ahi
          // se descartaban todos los siguientes de ese export: un aviso de
          // progreso hacia que el DONE real se tirase y el modal se quedaba
          // colgado con el archivo ya generado.
          if (isTerminal(normalizedData.status)) {
            markProcessed(normalizedData._id);
          }

          // Check if ANY modal is currently open (both info and download modals)
          const isAnyModalOpen = exportState.isInfoModalOpen || exportState.isDownloadModalOpen;

          if (!isAnyModalOpen) {
            // Update state but keep modals closed
            setExportState((prev) => ({
              ...prev,
              exportData: normalizedData,
              isInfoModalOpen: false,
              isDownloadModalOpen: false,
            }));

            // Allow toast notification to show (useNotifications will handle it)
            // The notification is already added to the state by useNotifications
          } else {
            // If status is DONE, close modal and trigger automatic download
            if (normalizedData.status === 'DONE' && exportState.config?.downloadFn) {
              // Update state and close modal
              setExportState((prev) => ({
                ...prev,
                exportData: normalizedData,
                isInfoModalOpen: false,
                isDownloadModalOpen: false,
              }));

              // Trigger automatic download
              handleAutoDownload(normalizedData, exportState.config.downloadFn);
            } else if (normalizedData.status === 'ERROR') {
              // El motivo del fallo se conserva. El backend persiste
              // {message, reference, data} en el job y lo manda en el aviso;
              // antes se cerraba el modal sin mostrar nada, asi que el usuario
              // solo veia que dejaba de esperar.
              setExportState((prev) => ({
                ...prev,
                exportData: normalizedData,
                error: normalizedData.error ?? { message: 'export.failed' },
                isInfoModalOpen: true,
                isDownloadModalOpen: false,
              }));
            } else {
              // For PENDING or other statuses, update state and keep modal open
              setExportState((prev) => ({
                ...prev,
                exportData: normalizedData,
                isInfoModalOpen: false,
                isDownloadModalOpen: true,
              }));
            }
          }
        }
      },
      [
        handleAutoDownload,
        exportState.isInfoModalOpen,
        exportState.isDownloadModalOpen,
        exportState.config,
        backgroundQueue,
        setBackgroundQueue,
      ],
    ),
  });

  const triggerExport = useCallback(
    (config: ExportConfig) => {
      // Clear any previous state
      // Ya no se limpia nada global: pedir un export nuevo no puede borrar el
      // rastro de los que siguen en vuelo.
      if (downloadLockRef.current) {
        window.exportDownloadLock?.delete(downloadLockRef.current);
        window.downloadTimestamps?.delete(downloadLockRef.current);
        downloadLockRef.current = null;
      }

      setExportState({
        isInfoModalOpen: true,
        isProcessing: false,
        isDownloadModalOpen: false,
        config,
        exportData: null,
        error: null,
        currentExportId: null,
      });
    },
    [],
  );

  const executeExport = useCallback(async () => {
    if (!exportState.config) return;

    setExportState((prev) => ({ ...prev, isProcessing: true, error: null }));

    try {
      const { exportHook } = exportState.config;
      const result = await exportHook();

      if (result?._id) {
        // Store the export ID for WebSocket comparison
        trackExport(result._id);
        exportIdRef.current = result._id;

        setExportState((prev) => ({
          ...prev,
          exportData: result,
          isInfoModalOpen: false,
          isProcessing: false,
          currentExportId: result._id,
        }));

        // If status is DONE, trigger automatic download immediately
        if (result.status === 'DONE') {
          handleAutoDownload(result, exportState.config.downloadFn);
        } else if (result.status === 'PENDING') {
          // Add to background queue immediately for PENDING status
          const currentQueue = backgroundQueue || [];
          const exists = currentQueue.some((item) => item._id === result._id);

          if (!exists) {
            const newItem: BackgroundExportItem = {
              _id: result._id,
              name: result.name || generateGenericFileName(result._id),
              status: result.status,
              addedAt: new Date().toISOString(),
              userId,
            };
            setBackgroundQueue([...currentQueue, newItem]);
          }

          // Show download modal for PENDING status
          setExportState((prev) => ({
            ...prev,
            isDownloadModalOpen: true,
          }));
        }
      }
    } catch (err) {
      console.error('❌ CentralizedExportProvider - Export failed:', err);
      setExportState((prev) => ({
        ...prev,
        error: err,
        isProcessing: false,
      }));
    }
  }, [exportState.config, handleAutoDownload, backgroundQueue, userId]);

  const value = useMemo(
    () => ({
      triggerExport,
      isModalOpen: exportState.isInfoModalOpen || exportState.isDownloadModalOpen,
      backgroundQueue: backgroundQueue || [],
      updateBackgroundQueue,
      pollingData,
      exportData: exportState.exportData,
    }),
    [
      triggerExport,
      exportState.isInfoModalOpen,
      exportState.isDownloadModalOpen,
      backgroundQueue,
      updateBackgroundQueue,
      pollingData,
      exportState.exportData,
    ],
  );

  return (
    <CentralizedExportContext.Provider value={value}>
      {children}
      {/* Export Information Alert Modal */}
      {exportState.isInfoModalOpen && exportState.config && (
        <InternalExportInformationAlert
          isOpen={exportState.isInfoModalOpen}
          config={exportState.config}
          onExport={executeExport}
          onClose={closeAllModals}
          isProcessing={exportState.isProcessing}
          error={exportState.error}
          exportData={exportState.exportData}
        />
      )}

      {/* Download Process Modal */}
      {exportState?.isDownloadModalOpen && exportState?.exportData && exportState?.config && (
        <DownloadFile
          key={exportState.exportData._id}
          isOpen={exportState.isDownloadModalOpen}
          onClose={closeAllModals}
          fnDownloadService={exportState.config.downloadFn}
          initResponse={exportState.exportData}
          onBackgroundDownload={() => {
            if (exportState.exportData) {
              const currentQueue = backgroundQueue || [];
              const exists = currentQueue.some((item) => item._id === exportState.exportData?._id);
              if (!exists) {
                const newItem: BackgroundExportItem = {
                  _id: exportState.exportData._id,
                  name: exportState.exportData.name || generateGenericFileName(exportState.exportData._id),
                  status: exportState.exportData.status || 'PENDING',
                  addedAt: new Date().toISOString(),
                  userId,
                };
                setBackgroundQueue([...currentQueue, newItem]);
              }
            }
          }}
        />
      )}
    </CentralizedExportContext.Provider>
  );
};

export const useCentralizedExport = (): CentralizedExportContextValue => {
  const context = useContext(CentralizedExportContext);
  if (!context) {
    throw new Error('useCentralizedExport must be used within CentralizedExportProvider');
  }
  return context;
};
